// src/pages/Settings/components/CoachMemorySection.jsx
// STATUS: active | UPDATED: 2026-08-29
// LOG:
//   2026-08-29  New. What the coach remembers, and a way to delete it.
//
// A coach that remembers you and will not tell you what it remembers is a
// coach people stop being honest with. This is the whole reason it is safe to
// let the model write about a person: they can read every line of it and
// remove any of it, in one place, without asking anyone.
//
// It also happens to be the fastest way to find out whether memory is any
// good. Nobody debugs a prompt by reading logs if they can read the four
// sentences the coach thinks are true about them.
//
// RLS does the scoping. coach_memories_self_all is user_id = auth.uid(), so
// this component never checks who anybody is.

import { useCallback, useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, IconButton, Spinner, Alert, AlertIcon, useToast,
} from '@chakra-ui/react'
import { Trash2, Brain } from 'lucide-react'
import SectionCard from './SectionCard.jsx'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

// Same four the database allows, in the order a person would want to read
// them: what I am working on first, what I am good at second.
const GROUPS = [
  { kind: 'struggle', label: 'Working on' },
  { kind: 'strength', label: 'Doing well' },
  { kind: 'goal', label: 'Aiming for' },
  { kind: 'fact', label: 'About how you work' },
]

export default function CoachMemorySection() {
  const { user } = useAuth()
  const toast = useToast()
  const [state, setState] = useState({ loading: true, rows: [] })
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!user?.id) {
      setState({ loading: false, rows: [] })
      return
    }
    const { data, error: readError } = await supabase
      .from('coach_memories')
      .select('id, kind, content, created_at')
      .order('created_at', { ascending: false })
    if (readError) {
      setError('Could not load what the coach remembers.')
      setState({ loading: false, rows: [] })
      return
    }
    setState({ loading: false, rows: data || [] })
  }, [user?.id])

  useEffect(() => {
    load()
  }, [load])

  const forget = async (id, content) => {
    setError(null)
    const { error: delError } = await supabase.from('coach_memories').delete().eq('id', id)
    if (delError) {
      setError('Could not delete that. Try again.')
      return
    }
    setState((s) => ({ ...s, rows: s.rows.filter((r) => r.id !== id) }))
    toast({
      title: 'Forgotten.',
      description: content.length > 60 ? `${content.slice(0, 60)}...` : content,
      status: 'success',
      duration: 3000,
    })
  }

  return (
    <SectionCard
      title="What the coach remembers"
      description="The coach keeps a short note when it learns something durable about how you sell. Delete anything you would rather it forgot."
    >
      {error && (
        <Alert status="error" borderRadius="md" fontSize="sm" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {state.loading ? (
        <Flex justify="center" py={8}>
          <Spinner size="md" color="ink" thickness="3px" />
        </Flex>
      ) : state.rows.length === 0 ? (
        <HStack bg="surface" borderRadius="card" px={5} py={4} spacing={3}>
          <Box color="inkMuted" flexShrink={0}>
            <Brain size={17} />
          </Box>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6}>
            Nothing yet. Notes appear here as the coach learns what you are working on, which
            takes a few real conversations.
          </Text>
        </HStack>
      ) : (
        <VStack align="stretch" spacing={5}>
          {GROUPS.map(({ kind, label }) => {
            const rows = state.rows.filter((r) => r.kind === kind)
            if (rows.length === 0) return null
            return (
              <Box key={kind}>
                <Text textStyle="eyebrow" color="inkMuted" mb={2}>
                  {label}
                </Text>
                <VStack align="stretch" spacing={0}>
                  {rows.map((r) => (
                    <HStack
                      key={r.id}
                      justify="space-between"
                      align="flex-start"
                      spacing={3}
                      py={3}
                      borderBottom="1px solid"
                      borderColor="line"
                      _last={{ borderBottom: 'none' }}
                    >
                      <Text fontSize="body-sm" color="ink" lineHeight={1.6}>
                        {r.content}
                      </Text>
                      <IconButton
                        aria-label={`Forget: ${r.content.slice(0, 40)}`}
                        icon={<Trash2 size={15} />}
                        size="sm"
                        minH="40px"
                        minW="40px"
                        variant="ghost"
                        color="inkDim"
                        _hover={{ color: 'danger', bg: 'surface' }}
                        onClick={() => forget(r.id, r.content)}
                      />
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )
          })}
        </VStack>
      )}
    </SectionCard>
  )
}
